import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
    Box, Typography, Paper, Container, Grid, 
    Chip, Button, Avatar, Stack, CircularProgress, 
    Fade, Zoom, IconButton, Tooltip, Divider, 
    TextField, InputAdornment
} from '@mui/material';
import {
    MenuBook as NotesIcon,
    Download as DownloadIcon,
    Person as TeacherIcon,
    Refresh as RefreshIcon,
    Folder as FolderIcon,
    Search as SearchIcon,
    InsertDriveFile as FileIcon,
    PictureAsPdf as PdfIcon,
    Description as DocIcon,
    Slideshow as PptIcon,
    CalendarMonth as DateIcon,
    History as HistoryIcon,
    ChevronRight as ArrowIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import axios from 'axios';
import { formatDatePart, getMonthNameFromDate } from '../../utils/customDate';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 100px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const SubjectFolder = styled(Paper)`
    padding: 24px;
    border-radius: 24px;
    background: ${props => props.active ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'white'};
    color: ${props => props.active ? 'white' : '#1e293b'};
    border: 1px solid ${props => props.active ? '#6366f1' : '#e2e8f0'};
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
    
    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        border-color: #6366f1;
    }
`;

const NoteItem = styled(Paper)`
    padding: 20px;
    border-radius: 20px;
    background: white;
    border: 1px solid #f1f5f9;
    transition: all 0.2s ease;
    
    &:hover {
        border-color: #6366f1;
        background: #f8fafc;
        transform: translateX(4px);
    }
`;

const StudentNotes = () => {
    const { currentUser } = useSelector((state) => state.user);
    const location = useLocation();
    
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSubject, setSelectedSubject] = useState(location.state?.selectedSubject || 'All');
    const [message, setMessage] = useState({ type: '', text: '' });

    const studentClassId = currentUser?.sclassName?._id;

    useEffect(() => {
        if (location.state?.selectedSubject) {
            setSelectedSubject(location.state.selectedSubject);
        }
    }, [location.state]);

    useEffect(() => {
        if (studentClassId) {
            fetchNotes();
        }
    }, [studentClassId]);

    const fetchNotes = async () => {
        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Notes/Student/${studentClassId}`);
            if (result.data && result.data.length > 0) {
                setNotes(result.data);
            } else {
                setNotes([]);
                setMessage({ type: 'info', text: 'No notes available yet' });
            }
        } catch (error) {
            console.error('Error fetching notes:', error);
            setMessage({ type: 'error', text: 'Error loading notes' });
        }
        setLoading(false);
    };

    const groupedNotes = useMemo(() => {
        const subjects = { 'All': notes };
        notes.forEach(note => {
            const sub = note.subject?.subName || 'General';
            if (!subjects[sub]) subjects[sub] = [];
            subjects[sub].push(note);
        });
        return subjects;
    }, [notes]);

    const filteredNotes = useMemo(() => {
        const baseNotes = groupedNotes[selectedSubject] || [];
        return baseNotes.filter(note => 
            note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            note.teacher?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [groupedNotes, selectedSubject, searchTerm]);

    const getFileIcon = (fileType) => {
        const type = fileType?.toLowerCase() || '';
        if (type.includes('pdf')) return <PdfIcon sx={{ color: '#ef4444' }} />;
        if (type.includes('doc')) return <DocIcon sx={{ color: '#3b82f6' }} />;
        if (type.includes('ppt')) return <PptIcon sx={{ color: '#f59e0b' }} />;
        return <FileIcon sx={{ color: '#64748b' }} />;
    };

    const handleDownload = (note) => {
        if (note.filePath) {
            const filename = note.filePath.split(/[/\\]/).pop();
            const fullUrl = `${process.env.REACT_APP_BASE_URL}/uploads/notes/${encodeURIComponent(filename)}`;
            window.open(fullUrl, '_blank');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                                    <NotesIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Study Materials
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        Access and download subject-wise lecture notes and resources.
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right', bgcolor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: '20px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                                <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Total Files</Typography>
                                <Typography variant="h3" sx={{ fontWeight: 900 }}>{notes.length}</Typography>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="xl" sx={{ mt: 5 }}>
                {/* Subject Folders */}
                <Box sx={{ mb: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 32, height: 32 }}><FolderIcon fontSize="small" /></Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Subject Folders</Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {Object.keys(groupedNotes).map((sub, idx) => (
                            <Grid item xs={6} sm={4} md={2.4} key={sub}>
                                <Zoom in timeout={300 + idx * 50}>
                                    <SubjectFolder 
                                        active={selectedSubject === sub}
                                        onClick={() => setSelectedSubject(sub)}
                                        elevation={0}
                                    >
                                        <Avatar sx={{ 
                                            bgcolor: selectedSubject === sub ? 'rgba(255,255,255,0.2)' : '#f8fafc',
                                            color: selectedSubject === sub ? 'white' : '#6366f1',
                                            width: 56, height: 56, mb: 1
                                        }}>
                                            <FolderIcon fontSize="large" />
                                        </Avatar>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, lineClamp: 1 }}>{sub}</Typography>
                                        <Chip 
                                            label={`${groupedNotes[sub].length} Files`} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: selectedSubject === sub ? 'rgba(255,255,255,0.2)' : '#f1f5f9',
                                                color: selectedSubject === sub ? 'white' : '#64748b',
                                                fontWeight: 800
                                            }} 
                                        />
                                    </SubjectFolder>
                                </Zoom>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Grid container spacing={4}>
                    {/* Search & Actions Sidebar */}
                    <Grid item xs={12} md={3}>
                        <Stack spacing={3}>
                            <Paper sx={{ p: 3, borderRadius: '24px', border: '1px solid #e2e8f0' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#64748b', textTransform: 'uppercase' }}>Filter Resources</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Search notes..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#94a3b8' }} />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: '12px', bgcolor: '#f8fafc' }
                                    }}
                                />
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchNotes}
                                    sx={{ mt: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                                >
                                    Refresh Notes
                                </Button>
                            </Paper>

                            <Paper sx={{ p: 3, borderRadius: '24px', bgcolor: '#1e293b', color: 'white' }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <HistoryIcon sx={{ color: '#6366f1' }} />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Recent Uploads</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Last synced just now</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>

                    {/* Notes Feed */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6' }}><NotesIcon /></Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>{selectedSubject} Notes</Typography>
                            </Box>
                        </Box>

                        <Stack spacing={2}>
                            {filteredNotes.length === 0 ? (
                                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: '24px', border: '1px dashed #cbd5e1' }}>
                                    <Avatar sx={{ width: 64, height: 64, bgcolor: '#f1f5f9', color: '#94a3b8', mx: 'auto', mb: 2 }}>
                                        <NotesIcon />
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>No notes found in this folder</Typography>
                                    <Typography variant="body2" color="textSecondary">Switch folders or try a different search term.</Typography>
                                </Paper>
                            ) : (
                                filteredNotes.map((note, index) => (
                                    <Fade in timeout={300 + index * 50} key={note._id}>
                                        <NoteItem elevation={0}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={1}>
                                                    <Avatar sx={{ bgcolor: '#f8fafc', color: '#6366f1', width: 48, height: 48 }}>
                                                        {getFileIcon(note.fileType)}
                                                    </Avatar>
                                                </Grid>
                                                <Grid item xs={12} sm={8}>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                                                        {note.title}
                                                    </Typography>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <TeacherIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                                                                {note.teacher?.name || 'Faculty'}
                                                            </Typography>
                                                        </Box>
                                                        <Divider orientation="vertical" flexItem sx={{ height: 12, my: 'auto' }} />
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <DateIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                                                                {formatDatePart(note.date || note.createdAt)}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12} sm={3} sx={{ textAlign: 'right' }}>
                                                    <Tooltip title="Download File">
                                                        <IconButton 
                                                            onClick={() => handleDownload(note)}
                                                            sx={{ 
                                                                bgcolor: '#6366f1', 
                                                                color: 'white',
                                                                '&:hover': { bgcolor: '#4f46e5' },
                                                                borderRadius: '12px'
                                                            }}
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Tooltip> 
                                                </Grid>
                                            </Grid>
                                        </NoteItem>
                                    </Fade>
                                ))
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </ContentContainer>
        </PageWrapper>
    );
};

export default StudentNotes;
